<script lang="ts" context="module">
  import {
    Column as Clmn,
    Config as Cfg,
    Pagination as Pg,
  } from "./types.svelte";
  // re-export
  export type Column = Clmn;
  export type Config = Cfg;
  export type Pagination = Pg;
</script>

<script lang="ts">
  import RenderTableHeader from "./RenderTableHeader.svelte";
  import RenderTableBody from "./RenderTableBody.svelte";
  import Paginator from "./Pagination.svelte";
  import CustomIcon from "../CustomIcon.svelte";
  import TableSettings from "./TableSettings.svelte";
  import TableHeadActions from "./TableHeadActions.svelte";
  import { setContext } from "svelte";
  import classNames from "../../utils/classNames";

  export let pagination: Pagination = {
    page: 1,
    pageSize: 10,
    count: 0,
  };

  export let sorting = {
    sortBy: "",
    sortOrder: "",
  };

  export let filters;

  export let defaultConfig: Config = {} as Config;
  let config: Config = { ...defaultConfig };
  export let data = [];
  export let dataKey = "id";
  export let columns: Column[] = [];
  export let selectedIds = [];
  export let loader = false;
  export let isChildTable = false;
  const defaultWidth = "110";
  if (!config.columnWidth) {
    config.columnWidth = defaultWidth;
  }

  $: loading = loader;
  $: _columns = [];
  let isAllSelected = false;
  let showSettings = false;

  let mainSettingsPagination: Pagination;

  const checkboxColumn = {
    renderBy: "checkbox",
    width: 40,
  };

  const dropdownColumn = {
    renderBy: "dropdown",
    width: 15,
  };

  function toggleSelectAll() {
    isAllSelected = !isAllSelected;
  }

  function toggleSettingsModal() {
    showSettings = !showSettings;
  }

  const updateWidth = (arr, { dataKey, width }) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].dataKey === dataKey) {
        return arr.map((item, index) => {
          if (index === i) {
            return { ...item, width };
          }
          return item;
        });
      }
    }
    return arr;
  };

  // function changeColumnsWidth(value, item) {
  //   const updateObject = {
  //     dataKey: item.dataKey,
  //     width: value,
  //   };
  //   columns = updateWidth(columns, updateObject);
  // }

  // function toggleFitContent(item) {
  //   let { fitContent, dataKey } = item;

  //   columns = columns.map((column) => {
  //     if (column.dataKey == dataKey) {
  //       column.fitContent = fitContent;
  //       if (fitContent) {
  //         delete column.width;
  //       } else {
  //         column.width = config.columnWidth;
  //       }
  //       // return column;
  //     }
  //     return column;
  //   });
  // }

  // function resetColumnsWidth() {
  //   columns = columns;
  // }

  function updateConfigItem(config: Config) {
    config = config;
    columns = columns;
  }

  function sortColumns(e: { detail: Column[] }) {
    const clmns = e.detail;
    // actions are not shown in columns reorder so add it
    const actions = columns.find((c) => c.renderBy === "actions");
    if (actions) {
      columns = [...clmns, actions];
    } else {
      columns = clmns;
    }
  }
  // general update columns method
  function onUpdateColumns(e: { detail: Column[] }) {
    const clmns = e.detail;
    columns = clmns;
  }

  function toggleColumnsVisibility(dataKey: string, isHidden: boolean) {
    columns = columns.map((c) =>
      c.renderBy !== "actions" && c.dataKey === dataKey ? { ...c, isHidden } : c
    );
    columns = columns;
  }

  $: {
    const customSettingArray = JSON.parse(
      localStorage.getItem(`_tableCustomSettings_${defaultConfig.tableId}`)
    );
    _columns = columns.map((c) => {
      if (!c.width) {
        c.width = config.columnWidth;
      }
      return c;
    }); //initialize columns
    const lastObject = _columns[_columns.length - 1]; //Get actions from object
    if (customSettingArray !== null) {
      // if storage data is available sort the columns using the settings from storage.
      const sortedColumns = [];
      customSettingArray.forEach((customColumn) => {
        const index = _columns.findIndex(
          (column) => column.dataKey === customColumn.dataKey
        );
        if(_columns[index]) sortedColumns.push(_columns[index]);
      });
      _columns = sortedColumns; // updated columns with sorted data
      _columns = _columns.map((column) => {
        const match = customSettingArray.find(
          (updatedColumn) => updatedColumn.dataKey === column.dataKey
        );
        if (match) {
          //update all fields with data from storage
          return {
            ...column,
            isHidden: match.isHidden,
            fitContent: match.fitContent,
            width: match.fitContent ? null : match.width,
          };
        }
        return column;
      });
      columns = _columns
      _columns.push(lastObject); // updated columns with actions
    }

    const mainSettingArray = JSON.parse(
      localStorage.getItem(`_tableMainSettings_${config.tableId}`)
    );

    config = {...config, ...mainSettingArray}

    if (config.showCheckboxes) {
      _columns = [checkboxColumn, ...columns];
    }

    if (config.enableSubTables) {
      _columns = [dropdownColumn, ..._columns];
    }

    if (config.hideActions) {
      _columns = _columns.filter((c) => c.renderBy !== "actions");
    }

    _columns = _columns.filter((c) => !c.isHidden);
    setContext("isChildTable", isChildTable);
  }

  $: if (selectedIds.length === 0) {
    isAllSelected = false;
  }
</script>

<TableHeadActions
  {filters}
  {config}
  deleteDisabled={selectedIds.length === 0}
  on:delete
  on:filterChange
/>

{#if _columns.length > 0 && !loading}
  <div
    class:z-10={isChildTable}
    class:h-[500px]={!isChildTable}
    class={classNames(
      "w-full bg-white overflow-x-auto rounded-md table-wrapper relative",
      $$props.class
    )}
  >
    <table
      class="w-full border-spacing-0 border-separate bg-white rounded-md"
      id={config.tableId}
    >
      <RenderTableHeader
        bind:columns={_columns}
        {config}
        {isChildTable}
        {isAllSelected}
        {...pagination}
        {...sorting}
        on:sortClick
        on:selectAll={toggleSelectAll}
      />

      <RenderTableBody
        bind:columns={_columns}
        {config}
        {data}
        {dataKey}
        {isAllSelected}
        on:subTableIdChange
        on:checkedChange
        on:actionClick
        on:columnClick
      >
        <span slot="sub_table">
          <slot name="sub_table" />
        </span>
      </RenderTableBody>
    </table>
  </div>

  {#if !isChildTable}
    <div class="flex flex-row justify-between items-center my-4">
      {#if config.tableId}
        <div>
          <button
            on:click={toggleSettingsModal}
            aria-label="table settings"
            type="button"
            class="w-14 h-14 relative inline-flex items-center justify-center py-2 rounded-md border
        shadow-nv border-custom-orange bg-custom-orange text-sm font-medium text-white hover:bg-custom-orange
        focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
          >
            <CustomIcon name="settings_white" />
          </button>
        </div>
      {/if}
      <Paginator {...pagination} on:pageChange />
    </div>
  {/if}
{/if}

<TableSettings
  columns={columns.filter((c) => c.renderBy !== "actions")}
  {defaultConfig}
  isVisible={showSettings}
  onCloseModal={toggleSettingsModal}
  onConfigChange={updateConfigItem}
  onSortColumns={sortColumns}
  onColumnVisibilityChange={toggleColumnsVisibility}
  on:pageChange
  on:updateColumns={onUpdateColumns}
  {pagination}
/>

<style>
  /* table {
    table-layout: fixed;
    width: max-content;
  } */
  .table-wrapper {
    /* always show/hide scrollbar to avoid resizing after data load */
    /* overflow-y: scroll; */

    /* or hide the scrollbars altogether */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none;
    height: calc(65dvh);
  }

  /* safari, chrome, new edge firefox */
  .table-wrapper::-webkit-scrollbar:vertical {
    display: none;
  }
  .table-wrapper::-webkit-scrollbar-thumb {
    background: #f7890794;
  }
</style>
